import {useAppDispatch, useAppSelector} from 'hooks'
import {
  selectLoading,
  selectInvoiceInformation,
  selectConsumer,
  selectBillingSummary,
  selectProvider,
  selectUsageDetail,
  selectProduct,
  selectTemplates,
  selectUsageSummaryData,
  setUsageSummaryData,
  selectError,
  setInvoiceInformation,
  setError,
  selectBaseFees,
  resetEmptyData,
  DataTableUsageSummary,
  getInvoiceData,
  createInvoice,
  updateInvoice,
  selectUsageSummaryTable,
} from 'features/invoice/invoiceViewSlice'
import {
  getInvoiceFileUploadUrl,
  InvoiceInformation,
  uploadInvoiceFileToS3,
} from 'api/productApi'

export function useInvoiceView() {
  const dispatch = useAppDispatch()

  const loading = useAppSelector(selectLoading)
  const error = useAppSelector(selectError)
  const invoiceInformation = useAppSelector(selectInvoiceInformation)
  const consumer = useAppSelector(selectConsumer)
  const billingSummary = useAppSelector(selectBillingSummary)
  const provider = useAppSelector(selectProvider)
  const usageDetail = useAppSelector(selectUsageDetail)
  const usageSummaryTable = useAppSelector(selectUsageSummaryTable)
  const product = useAppSelector(selectProduct)
  const templates = useAppSelector(selectTemplates)
  const usageSummaryData = useAppSelector(selectUsageSummaryData)
  const baseFees = useAppSelector(selectBaseFees)

  const getInvoice = (orgId: string, billingMonth: string) => {
    dispatch(getInvoiceData({orgId, billingMonth}))
  }

  const onSaveInvoice = async (invoiceBlob: Blob) => {
    const {consumer, billing} = invoiceInformation || {}
    if (!consumer?.orgId || !billing?.billingMonth) return
    const fileUploadUrlRes = await getInvoiceFileUploadUrl({
      orgId: consumer.orgId.toString(),
      billingMonth: billing.billingMonth,
    })
    const {uploadUrl, fileKey} = fileUploadUrlRes.data || {}
    const uploadResponse = await uploadInvoiceFileToS3({
      path: uploadUrl,
      data: invoiceBlob,
    })
    const etag = uploadResponse?.headers?.etag
    if (!etag) return

    const payload = {
      billingMonth: billing.billingMonth,
      billingPrice: billing.billingPrice || 0,
      issueDate: billing.issueDate || '',
      remarks: billing.remarks || '',
      registerReportFile: {
        etag,
        key: fileKey || '',
      },
    }

    if (invoiceInformation?.id) {
      // update if id exists
      await dispatch(
        updateInvoice({
          invoiceId: invoiceInformation.id,
          ...payload,
        }),
      ).unwrap()
      return
    }

    // create new invoice
    await dispatch(
      createInvoice({
        orgId: consumer.orgId,
        ...payload,
      }),
    ).unwrap()
  }

  const updateUsageSummaryAction = (data: DataTableUsageSummary[]) => {
    dispatch(setUsageSummaryData(data))
  }

  const callSetInvoiceInformation = (info: Partial<InvoiceInformation>) => {
    dispatch(setInvoiceInformation(info))
  }

  const resetInvoiceDataAction = () => {
    dispatch(resetEmptyData())
  }

  return {
    getInvoice,
    error,
    loading,
    baseFees,
    invoiceInformation,
    callSetInvoiceInformation,
    consumer,
    billingSummary,
    provider,
    usageDetail,
    usageSummaryTable,
    product,
    templates,
    usageSummaryData,
    onSaveInvoice,
    updateUsageSummaryAction,
    resetInvoiceDataAction,
    setError,
  }
}

export default useInvoiceView
